import React from 'react';
import {useTranslation} from 'react-i18next';

import type {ButtonProps, LogoSizeModifier} from '@pexip/components';
import {Icon, IconTypes} from '@pexip/components';

import {TestId} from '../../../../test/testIds';
import {useShortcutTranslation} from '../../../hooks/useShortcutTranslation';
import {InMeetingInputControl} from '../../InputControl/InMeetingInputControl.view';

export const RaiseHandButton: React.FC<
    ButtonProps & {
        isHandRaised: boolean;
        isRaiseHandHidden?: boolean;
        onRaiseHand: (raise: boolean) => void;
        iconSize: LogoSizeModifier;
        tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
    }
> = ({
    isHandRaised,
    isRaiseHandHidden = false,
    onRaiseHand,
    iconSize,
    tooltipPosition = 'top',
    ...props
}) => {
    const {t} = useTranslation();
    const shortcut = useShortcutTranslation('R');

    if (isRaiseHandHidden) {
        return null;
    }

    const actionLabel = isHandRaised
        ? t('common.lower-hand', 'Lower hand')
        : t('common.raise-hand', 'Raise hand');
    const label = shortcut ? `${actionLabel} ${shortcut}` : actionLabel;

    return (
        <InMeetingInputControl
            icon={<Icon source={IconTypes.IconRaiseHand} size={iconSize} />}
            aria-label={label}
            tooltipText={label}
            tooltipPosition={tooltipPosition}
            onClick={() => onRaiseHand(!isHandRaised)}
            isActive={isHandRaised}
            data-testid={
                isHandRaised ? TestId.ButtonLowerHand : TestId.ButtonRaiseHand
            }
            {...props}
        />
    );
};

export type RaiseHandButtonProps = React.ComponentProps<typeof RaiseHandButton>;
